import React, { useEffect, useMemo, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCenter,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from './supabaseClient';

async function supaFetchManualStaffing(userId: string) {
    try {
        if (!supabase) return null;
        const { data: files, error } = await supabase
            .from('file_metadata')
            .select('*')
            .eq('user_id', userId)
            .eq('file_name', 'manual_staffing.json');
        if (error) throw error;
        if (files && files.length > 0) {
            return JSON.parse(files[0].content || "{}");
        }
        return null;
    } catch (e) {
        console.error("Error fetching manual staffing data:", e);
        return null;
    }
}

async function supaSaveManualStaffing(userId: string, venueId: string, content: any) {
    try {
        if (!supabase) return false;
        const { data: files } = await supabase
            .from('file_metadata')
            .select('id')
            .eq('user_id', userId)
            .eq('file_name', 'manual_staffing.json');

        const existingId = files?.[0]?.id;

        const { error: saveError } = await supabase
            .from('file_metadata')
            .upsert({
                ...(existingId ? { id: existingId } : {}),
                user_id: userId,
                venue_id: (venueId && venueId !== "" && venueId !== "null") ? venueId : null,
                file_name: 'manual_staffing.json',
                storage_path: 'local/manual_staffing.json',
                file_type: 'application/json',
                content: JSON.stringify(content),
                tags: ['Manual Data', 'Staffing'],
                size: JSON.stringify(content).length,
                created_at: new Date().toISOString()
            });

        if (saveError) throw saveError;
        return true;
    } catch (e) {
        console.error("Error saving manual staffing data:", e);
        return false;
    }
}

type StaffAvailability = {
    available: boolean;
    start: string;
    end: string;
};

type StaffPerformance = {
    lateness: number;
    daysOff: number;
    illness: number;
};

type StaffMember = {
    id: string;
    name: string;
    role: string;
    salary: number;
    contractedHours: number;
    availability: Record<string, StaffAvailability>;
    performance: StaffPerformance;
    avatarUrl?: string;
    colorIndex?: number;
};

const STAFF_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const buildDefaultAvailability = () => {
    const avail: Record<string, StaffAvailability> = {};
    STAFF_DAYS.forEach((day) => {
        avail[day] = {
            available: day !== "Sun",
            start: "10:00",
            end: "22:00"
        };
    });
    return avail;
};

const normalizeAvailability = (input: any) => {
    const base = buildDefaultAvailability();
    if (!input || typeof input !== "object") return base;
    const next: Record<string, StaffAvailability> = { ...base };
    STAFF_DAYS.forEach((day) => {
        if (input[day]) {
            next[day] = {
                available: input[day].available ?? base[day].available,
                start: input[day].start || base[day].start,
                end: input[day].end || base[day].end
            };
        }
    });
    return next;
};

const normalizeStaffMember = (input: any): StaffMember => {
    const rawName = input && Object.prototype.hasOwnProperty.call(input, "name")
        ? input.name
        : (typeof input === "string" ? input : "");
    const name = String(rawName ?? "").trim();
    const role = String(input?.role || "FOH");
    return {
        id: String(input?.id || `staff_${Math.random().toString(36).slice(2, 9)}`),
        name: name || "New Staff",
        role,
        salary: Number(input?.salary ?? 0),
        contractedHours: Number(input?.contractedHours ?? 0),
        availability: normalizeAvailability(input?.availability),
        performance: {
            lateness: Number(input?.performance?.lateness ?? 0),
            daysOff: Number(input?.performance?.daysOff ?? 0),
            illness: Number(input?.performance?.illness ?? 0)
        },
        avatarUrl: typeof input?.avatarUrl === "string" ? input.avatarUrl : "",
        colorIndex: Number.isFinite(input?.colorIndex) ? Number(input.colorIndex) : undefined
    };
};

const DEFAULT_STAFF_SEED: Array<Partial<StaffMember>> = [
    { name: "Marco R.", role: "Kitchen", salary: 28000, contractedHours: 40 },
    { name: "Sarah L.", role: "FOH", salary: 26000, contractedHours: 35 },
    { name: "James K.", role: "Supervisor", salary: 32000, contractedHours: 42 },
    { name: "Elena M.", role: "Kitchen", salary: 25500, contractedHours: 30 },
    { name: "Tom S.", role: "Bar", salary: 24000, contractedHours: 25 },
    { name: "Claire D.", role: "FOH", salary: 23000, contractedHours: 20 }
];

const DEFAULT_STAFF = DEFAULT_STAFF_SEED.map((entry, idx) =>
    normalizeStaffMember({ id: `staff_${idx}`, ...entry })
);

const initialsForName = (name: string) => {
    const parts = name.split(" ").filter(Boolean);
    if (parts.length === 0) return "NA";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const minutesFromTime = (time: string) => {
    if (!time) return null;
    const [h, m] = time.split(":").map((v) => Number(v));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
};

const shiftDurationHours = (start: string, end: string) => {
    const startMin = minutesFromTime(start);
    const endMin = minutesFromTime(end);
    if (startMin === null || endMin === null) return 0;
    let diff = endMin - startMin;
    if (diff < 0) diff += 24 * 60;
    return diff / 60;
};

const formatMoney = (value: number) => {
    if (!Number.isFinite(value)) return "£0";
    return `£${Math.round(value).toLocaleString()}`;
};

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day + 6) % 7;
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const formatShortDate = (date: Date) =>
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const formatMonthYear = (date: Date) =>
    date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const getMonthWeekStarts = (date: Date) => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1);
    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const starts: Date[] = [];
    let cursor = startOfWeek(first);
    while (cursor <= last) {
        starts.push(new Date(cursor));
        cursor = addDays(cursor, 7);
    }
    return starts;
};

const formatWeekRange = (start: Date) => {
    const end = addDays(start, 6);
    const sameMonth = start.getMonth() === end.getMonth();
    const year = start.getFullYear();
    if (sameMonth) {
        return `${formatShortDate(start)}–${end.getDate()}, ${year}`;
    }
    return `${formatShortDate(start)}–${formatShortDate(end)}, ${year}`;
};

const weatherMultiplier = (weather: string) => {
    if (weather.includes("☀️")) return 1.1;
    if (weather.includes("🌧️")) return 0.9;
    if (weather.includes("☁️")) return 1.0;
    return 1.0;
};

const intensityMultiplier = (intensity: string) => {
    if (intensity === "Peak") return 1.15;
    if (intensity === "High") return 1.08;
    if (intensity === "Medium") return 1.0;
    return 0.95;
};

const recommendedStaffCount = (covers: number, weather: string, intensity: string) => {
    const base = covers > 0 ? covers / 35 : 2;
    const rec = Math.round(base * weatherMultiplier(weather) * intensityMultiplier(intensity));
    return Math.max(2, rec);
};

const dailyWageFromSalary = (salary: number) => {
    if (!Number.isFinite(salary) || salary <= 0) return 0;
    return salary / 260;
};

const availabilitySummary = (availability: Record<string, StaffAvailability>) => {
    const availableDays = STAFF_DAYS.filter((d) => availability?.[d]?.available);
    if (availableDays.length === 0) return "No availability set";
    const first = availability[availableDays[0]];
    const sameTime = availableDays.every((d) => availability[d]?.start === first?.start && availability[d]?.end === first?.end);
    if (availableDays.length === 5 && availableDays.slice(0, 5).join(",") === "Mon,Tue,Wed,Thu,Fri" && sameTime) {
        return `Mon–Fri ${first?.start || "--"}–${first?.end || "--"}`;
    }
    if (availableDays.length === 7 && sameTime) {
        return `All week ${first?.start || "--"}–${first?.end || "--"}`;
    }
    return `Custom (${availableDays.join(", ")})`;
};

function ShiftDraggable({
    shift,
    onEdit
}: {
    shift: any;
    onEdit?: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `shift_${shift.id}`,
        data: { type: "shift", shiftId: shift.id }
    });

    const sanitizedTransform = transform
        ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)`
        : undefined;
    const style: React.CSSProperties = {
        transform: sanitizedTransform ? sanitizedTransform : undefined,
        opacity: isDragging ? 0 : 1,
        pointerEvents: isDragging ? "none" : "auto",
        willChange: "transform",
        transition: isDragging ? "none" : "transform 150ms ease"
    };

    return buildShiftCard(shift, {
        ref: setNodeRef,
        style,
        ...attributes,
        ...listeners,
        className: "cursor-grab active:cursor-grabbing group relative",
        onDoubleClick: () => onEdit?.(),
        onEdit
    });
}

function ShiftDropCell({
    staffId,
    day,
    selected,
    children
}: {
    staffId: string;
    day: string;
    selected: boolean;
    children: React.ReactNode;
}) {
    const { setNodeRef, isOver } = useDroppable({
        id: `cell:${staffId}:${day}`
    });

    return React.createElement("div", {
        ref: setNodeRef,
        className: `flex-1 p-2 border-r last:border-0 border-gray-50 flex flex-col gap-2 relative group hover:bg-gray-50/50 transition-all ${selected ? 'bg-indigo-50/20' : ''} ${isOver ? 'ring-2 ring-indigo-400/60' : ''}`
    }, children);
}

const STAFF_COLOR_TONES = [
    "bg-indigo-50 text-indigo-700 border-indigo-100",
    "bg-emerald-50 text-emerald-700 border-emerald-100",
    "bg-amber-50 text-amber-700 border-amber-100",
    "bg-rose-50 text-rose-700 border-rose-100",
    "bg-sky-50 text-sky-700 border-sky-100",
    "bg-purple-50 text-purple-700 border-purple-100",
    "bg-teal-50 text-teal-700 border-teal-100"
];

const STAFF_COLOR_BG = [
    "bg-indigo-50/70",
    "bg-emerald-50/70",
    "bg-amber-50/70",
    "bg-rose-50/70",
    "bg-sky-50/70",
    "bg-purple-50/70",
    "bg-teal-50/70"
];

const staffColorIndex = (name: string) => {
    const base = (name || "").trim().toLowerCase();
    let hash = 0;
    for (let i = 0; i < base.length; i += 1) {
        hash = (hash * 31 + base.charCodeAt(i)) % 2147483647;
    }
    return STAFF_COLOR_TONES.length ? Math.abs(hash) % STAFF_COLOR_TONES.length : 0;
};

const normalizeColorIndex = (idx: number | undefined) => {
    if (!Number.isFinite(idx)) return undefined;
    const safe = Number(idx);
    if (STAFF_COLOR_TONES.length === 0) return undefined;
    return Math.abs(safe) % STAFF_COLOR_TONES.length;
};

const pickAvailableColorIndex = (used: Set<number>) => {
    for (let i = 0; i < STAFF_COLOR_TONES.length; i += 1) {
        if (!used.has(i)) {
            used.add(i);
            return i;
        }
    }
    const fallback = STAFF_COLOR_TONES.length ? used.size % STAFF_COLOR_TONES.length : 0;
    used.add(fallback);
    return fallback;
};

const assignMissingStaffColors = (list: StaffMember[]) => {
    const used = new Set<number>();
    const next = list.map((staff) => {
        const existing = normalizeColorIndex(staff.colorIndex);
        if (existing !== undefined) {
            used.add(existing);
            return { ...staff, colorIndex: existing };
        }
        const assigned = pickAvailableColorIndex(used);
        return { ...staff, colorIndex: assigned };
    });
    return next;
};

function shiftToneClass(shift: any) {
    const fromShift = normalizeColorIndex(shift?.colorIndex);
    const idx = fromShift !== undefined ? fromShift : staffColorIndex(shift?.staff || "");
    return STAFF_COLOR_TONES[idx] || "bg-gray-50 text-gray-700 border-gray-100";
}

function buildShiftCard(shift: any, props?: (React.HTMLAttributes<HTMLDivElement> & { onEdit?: () => void; ref?: any })) {
    const { className, style, onEdit, ...rest } = props || {};
    const mergedClass = [
        "p-2 rounded-lg text-[10px] font-bold border shadow-sm transition-colors",
        shiftToneClass(shift),
        className
    ].filter(Boolean).join(" ");

    return React.createElement("div", {
        ...rest,
        className: mergedClass,
        style
    }, [
        onEdit && React.createElement("button", {
            className: "absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 border border-slate-200 text-slate-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity",
            onPointerDown: (e: any) => e.stopPropagation(),
            onClick: (e: any) => { e.stopPropagation(); onEdit(); },
            title: "Edit shift",
            "aria-label": "Edit shift"
        }, React.createElement("svg", {
            viewBox: "0 0 20 20",
            className: "w-3.5 h-3.5"
        }, React.createElement("path", {
            d: "M12.7 3.3l4 4-8.9 8.9H3.8v-3.9L12.7 3.3z",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "1.6",
            strokeLinecap: "round",
            strokeLinejoin: "round"
        }))),
        React.createElement("div", { className: "flex justify-between items-start" }, [
            React.createElement("span", { className: "text-xs" }, `${shift.startTime} - ${shift.endTime}`),
            null
        ]),
        React.createElement("div", { className: "mt-1 flex justify-between uppercase opacity-70" }, [
            React.createElement("span", {}, shift.role),
            React.createElement("span", {}, `£${shift.cost}`)
        ])
    ]);
}

function StaffRowSortable({
    staff,
    days,
    shifts,
    selectedDay,
    scheduledHours,
    onOpenShiftModal,
    onEdit,
    onRemove,
    expanded,
    onToggleExpand,
}: {
    staff: StaffMember;
    days: Array<{ name: string }>;
    shifts: any[];
    selectedDay: string;
    scheduledHours: number;
    onOpenShiftModal: (name: string, day: string, shift?: any) => void;
    onEdit: (staff: StaffMember) => void;
    onRemove: (staffId: string) => void;
    expanded: boolean;
    onToggleExpand: (staffId: string) => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: staff.id,
        data: { type: "staff" }
    });
    const dailyWage = dailyWageFromSalary(staff.salary);
    const overContract = staff.contractedHours > 0 && scheduledHours > staff.contractedHours;
    const availability = availabilitySummary(staff.availability);

    return React.createElement("div", {
        ref: setNodeRef,
        style: {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.7 : 1
        },
        className: "flex min-h-[96px] bg-white"
    }, [
        React.createElement("div", { className: `w-64 p-4 border-r border-gray-100 flex items-start gap-3 group ${STAFF_COLOR_BG[normalizeColorIndex(staff.colorIndex) ?? staffColorIndex(staff.name)] || "bg-gray-50/30"}` }, [
            React.createElement("button", {
                className: "mt-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing",
                ...attributes,
                ...listeners,
                title: "Drag to reorder"
            }, "⋮⋮"),
            React.createElement("div", { className: "w-9 h-9 rounded-full overflow-hidden bg-white border border-slate-200 flex items-center justify-center text-xs font-black text-indigo-600" },
                staff.avatarUrl
                    ? React.createElement("img", { src: staff.avatarUrl, alt: staff.name, className: "w-full h-full object-cover" })
                    : React.createElement("span", {}, initialsForName(staff.name))
            ),
            React.createElement("div", { className: "flex-1 min-w-0" }, [
                React.createElement("div", { className: "flex items-start justify-between gap-3" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-sm font-black text-slate-800 truncate" }, staff.name),
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, staff.role || "Role")
                    ]),
                    React.createElement("div", { className: "flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity" }, [
                        React.createElement("button", {
                            className: "w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 shadow-sm flex items-center justify-center transition-colors",
                            onClick: () => onToggleExpand(staff.id),
                            title: expanded ? "Hide details" : "Show details",
                            "aria-label": expanded ? "Hide details" : "Show details"
                        }, React.createElement("svg", {
                            viewBox: "0 0 20 20",
                            className: `w-4 h-4 ${expanded ? "rotate-180" : ""} transition-transform`
                        }, React.createElement("path", {
                            d: "M5 7l5 6 5-6",
                            fill: "none",
                            stroke: "currentColor",
                            strokeWidth: "2",
                            strokeLinecap: "round",
                            strokeLinejoin: "round"
                        }))),
                        React.createElement("button", {
                            className: "w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 shadow-sm flex items-center justify-center transition-colors",
                            onClick: () => onEdit(staff),
                            title: "Edit staff",
                            "aria-label": "Edit staff"
                        }, React.createElement("svg", {
                            viewBox: "0 0 20 20",
                            className: "w-4 h-4"
                        }, React.createElement("path", {
                            d: "M12.7 3.3l4 4-8.9 8.9H3.8v-3.9L12.7 3.3z",
                            fill: "none",
                            stroke: "currentColor",
                            strokeWidth: "1.8",
                            strokeLinecap: "round",
                            strokeLinejoin: "round"
                        }))),
                        React.createElement("button", {
                            className: "w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-rose-600 hover:border-rose-200 shadow-sm flex items-center justify-center transition-colors",
                            onClick: () => onRemove(staff.id),
                            title: "Remove staff",
                            "aria-label": "Remove staff"
                        }, React.createElement("svg", {
                            viewBox: "0 0 20 20",
                            className: "w-4 h-4"
                        }, React.createElement("path", {
                            d: "M4 6h12M8 6V4h4v2m-5 3v6m6-6v6M6 6l1 10h6l1-10",
                            fill: "none",
                            stroke: "currentColor",
                            strokeWidth: "1.6",
                            strokeLinecap: "round",
                            strokeLinejoin: "round"
                        })))
                    ])
                ]),
                expanded && React.createElement("div", { className: "mt-2 grid grid-cols-2 gap-2 text-[10px]" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-slate-400 uppercase font-black tracking-widest" }, "Salary"),
                        React.createElement("p", { className: "text-slate-700 font-bold" }, staff.salary ? formatMoney(staff.salary) : "—")
                    ]),
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-slate-400 uppercase font-black tracking-widest" }, "Daily Wage"),
                        React.createElement("p", { className: "text-slate-700 font-bold" }, dailyWage > 0 ? formatMoney(dailyWage) : "—")
                    ])
                ]),
                expanded && React.createElement("div", { className: "mt-2 flex flex-wrap items-center gap-2 text-[10px]" }, [
                    React.createElement("span", { className: `px-2 py-1 rounded-full font-black uppercase tracking-widest border ${overContract ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"}` },
                        `Scheduled ${scheduledHours.toFixed(1)}h`
                    ),
                    React.createElement("span", { className: "px-2 py-1 rounded-full font-black uppercase tracking-widest bg-indigo-50 text-indigo-700 border border-indigo-200" },
                        `Contract ${staff.contractedHours || 0}h`
                    ),
                    overContract && React.createElement("span", { className: "px-2 py-1 rounded-full font-black uppercase tracking-widest bg-rose-100 text-rose-700 border border-rose-200" }, "Over Contract")
                ]),
                expanded && React.createElement("div", { className: "mt-2 text-[10px] text-slate-500 font-semibold" }, `Availability: ${availability}`),
                expanded && React.createElement("div", { className: "mt-1 text-[10px] text-slate-400 font-semibold" },
                    `Performance: ${staff.performance.lateness} late • ${staff.performance.daysOff} off • ${staff.performance.illness} illness`
                )
            ])
        ]),
        ...days.map((d) => {
            const staffShifts = shifts.filter((s) => s.staff === staff.name && s.day === d.name);
            return React.createElement(ShiftDropCell, {
                key: d.name,
                staffId: staff.id,
                day: d.name,
                selected: selectedDay === d.name,
                children: [
                    staffShifts.map((shift) =>
                        React.createElement(ShiftDraggable, {
                            key: shift.id,
                            shift: { ...shift, colorIndex: staff.colorIndex },
                            onEdit: () => onOpenShiftModal(staff.name, d.name, shift)
                        })
                    ),
                    staffShifts.length === 0 && React.createElement("button", {
                        className: "absolute bottom-2 right-2 w-7 h-7 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-colors",
                        onClick: () => onOpenShiftModal(staff.name, d.name)
                    }, "＋")
                ]
            });
        })
    ]);
}

export function StaffingView({ user: _user, venueId: _venueId, onNavigate: _onNavigate }: { user: any; venueId: string; onNavigate: (v: any) => void }) {
    const [loading, setLoading] = useState(true);

    // Original TeamsView state (hidden for now)
    const [showLegacyTeams] = useState(false);

    // New Staffing State
    const [selectedDay, setSelectedDay] = useState("Fri");
    const [activeTab, setActiveTab] = useState<"rota" | "performance">("rota");
    const [calendarView, setCalendarView] = useState<"week" | "month">("week");
    const [calendarAnchor, setCalendarAnchor] = useState<Date>(() => new Date());
    const [monthWeekIndex, setMonthWeekIndex] = useState(0);
    const [expandedStaffIds, setExpandedStaffIds] = useState<Set<string>>(new Set());
    const [shifts, setShifts] = useState<any[]>([
        { id: 1, staff: "Marco R.", role: "Kitchen", startTime: "10:00", endTime: "16:00", day: "Fri", cost: 72, type: "Original" },
        { id: 2, staff: "Sarah L.", role: "FOH", startTime: "12:00", endTime: "22:00", day: "Fri", cost: 110, type: "Original" },
        { id: 3, staff: "James K.", role: "Supervisor", startTime: "09:00", endTime: "17:00", day: "Fri", cost: 120, type: "Original" },
        { id: 4, staff: "Elena M.", role: "Kitchen", startTime: "17:00", endTime: "23:00", day: "Fri", cost: 85, type: "Added" },
    ]);
    const [activeShiftId, setActiveShiftId] = useState<string | null>(null);

    const [staffMembers, setStaffMembers] = useState<StaffMember[]>(assignMissingStaffColors(DEFAULT_STAFF));
    const [showShiftModal, setShowShiftModal] = useState(false);
    const [showAdjustment, setShowAdjustment] = useState(false);
    const [lastShift, setLastShift] = useState<any | null>(null);
    const [editingShiftId, setEditingShiftId] = useState<string | null>(null);
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<StaffMember | null>(null);
    const [staffDraft, setStaffDraft] = useState<StaffMember>(normalizeStaffMember({ name: "" }));
    const [shiftDraft, setShiftDraft] = useState({
        staff: "",
        role: "FOH",
        startTime: "",
        endTime: "",
        day: "Fri",
        cost: "",
        type: "Added"
    });
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const dayMeta = {
        Mon: { covers: 92, weather: "☁️", intensity: "Low" },
        Tue: { covers: 98, weather: "🌧️", intensity: "Low" },
        Wed: { covers: 110, weather: "☁️", intensity: "Medium" },
        Thu: { covers: 135, weather: "☀️", intensity: "Medium" },
        Fri: { covers: 215, weather: "☀️", intensity: "Peak" },
        Sat: { covers: 240, weather: "☀️", intensity: "Peak" },
        Sun: { covers: 180, weather: "🌧️", intensity: "High" }
    } as Record<string, { covers: number; weather: string; intensity: string }>;

    const monthWeekStarts = useMemo(() => {
        if (calendarView !== "month") return [];
        const firstOfMonth = new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth(), 1);
        return getMonthWeekStarts(firstOfMonth);
    }, [calendarView, calendarAnchor]);

    const weekStart = useMemo(() => {
        if (calendarView === "week") return startOfWeek(calendarAnchor);
        const starts = monthWeekStarts;
        if (starts.length === 0) return startOfWeek(calendarAnchor);
        const safeIndex = Math.min(Math.max(monthWeekIndex, 0), starts.length - 1);
        return starts[safeIndex];
    }, [calendarView, calendarAnchor, monthWeekStarts, monthWeekIndex]);

    const days = useMemo(() => {
        const dayAssignments: Record<string, number> = {};
        WEEK_DAYS.forEach((day) => {
            const assigned = new Set<string>();
            shifts.forEach((shift) => {
                if (shift.day === day && shift.staff) assigned.add(shift.staff);
            });
            dayAssignments[day] = assigned.size;
        });

        return WEEK_DAYS.map((name, idx) => {
            const date = addDays(weekStart, idx);
            const meta = dayMeta[name] || { covers: 0, weather: "—", intensity: "Low" };
            const assigned = dayAssignments[name] || 0;
            const recommended = recommendedStaffCount(meta.covers || 0, meta.weather || "", meta.intensity || "Low");
            const gap = assigned - recommended;
            return {
                name,
                date,
                dateLabel: formatShortDate(date),
                assigned,
                recommended,
                gap,
                ...meta
            };
        });
    }, [weekStart, shifts]);

    const targetPercent = 22;
    const forecastRevenue = 38500;
    const totalWeeklyCost = shifts.reduce((acc, shift) => acc + (Number(shift.cost) || 0), 0);
    const labourPercent = Math.round((totalWeeklyCost / Math.max(1, forecastRevenue)) * 100);
    const stats = {
        weeklyCost: `£${totalWeeklyCost.toLocaleString()}`,
        labourPercent,
        targetPercent,
        forecastRev: `£${forecastRevenue.toLocaleString()}`,
        status: labourPercent > targetPercent ? "Over Budget" : "On Target"
    };

    const staffScheduledHours = useMemo(() => {
        const map: Record<string, number> = {};
        staffMembers.forEach((staff) => {
            const total = shifts
                .filter((s) => s.staff === staff.name)
                .reduce((acc, s) => acc + shiftDurationHours(s.startTime, s.endTime), 0);
            map[staff.id] = total;
        });
        return map;
    }, [staffMembers, shifts]);

    const performanceOverview = useMemo(() => {
        const totals = staffMembers.reduce((acc, staff) => {
            acc.lateness += staff.performance?.lateness || 0;
            acc.daysOff += staff.performance?.daysOff || 0;
            acc.illness += staff.performance?.illness || 0;
            return acc;
        }, { lateness: 0, daysOff: 0, illness: 0 });

        const topLateness = [...staffMembers].sort((a, b) => (b.performance?.lateness || 0) - (a.performance?.lateness || 0))[0];
        const topDaysOff = [...staffMembers].sort((a, b) => (b.performance?.daysOff || 0) - (a.performance?.daysOff || 0))[0];
        const topIllness = [...staffMembers].sort((a, b) => (b.performance?.illness || 0) - (a.performance?.illness || 0))[0];

        return {
            totals,
            topLateness,
            topDaysOff,
            topIllness
        };
    }, [staffMembers]);

    const weekRangeLabel = useMemo(() => formatWeekRange(weekStart), [weekStart]);
    const monthLabel = useMemo(() => formatMonthYear(calendarAnchor), [calendarAnchor]);

    const handlePrevRange = () => {
        if (calendarView === "week") {
            setCalendarAnchor(addDays(calendarAnchor, -7));
            return;
        }
        const next = new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() - 1, 1);
        setCalendarAnchor(next);
        setMonthWeekIndex(0);
    };

    const handleNextRange = () => {
        if (calendarView === "week") {
            setCalendarAnchor(addDays(calendarAnchor, 7));
            return;
        }
        const next = new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + 1, 1);
        setCalendarAnchor(next);
        setMonthWeekIndex(0);
    };

    const activeShift = useMemo(() => {
        if (!activeShiftId) return null;
        const found = shifts.find((shift) => String(shift.id) === String(activeShiftId)) || null;
        if (!found) return null;
        const staff = staffMembers.find((s) => s.name === found.staff);
        return staff ? { ...found, colorIndex: staff.colorIndex } : found;
    }, [activeShiftId, shifts, staffMembers]);

    const handleDragStart = (event: any) => {
        const activeType = event?.active?.data?.current?.type;
        if (activeType === "shift") {
            const shiftId = event?.active?.data?.current?.shiftId;
            if (shiftId !== undefined && shiftId !== null) {
                setActiveShiftId(String(shiftId));
            }
        }
    };

    const handleDragEnd = async (event: any) => {
        const { active, over } = event || {};
        if (!active || !over) {
            setActiveShiftId(null);
            return;
        }
        if (active.id === over.id) {
            setActiveShiftId(null);
            return;
        }

        const activeType = active.data?.current?.type;
        if (activeType === "staff") {
            const oldIndex = staffMembers.findIndex((s) => s.id === active.id);
            const newIndex = staffMembers.findIndex((s) => s.id === over.id);
            if (oldIndex < 0 || newIndex < 0) {
                setActiveShiftId(null);
                return;
            }
            const nextStaff = arrayMove(staffMembers, oldIndex, newIndex);
            setStaffMembers(nextStaff);
            await persistStaffing(nextStaff, shifts);
            setActiveShiftId(null);
            return;
        }

        if (activeType === "shift") {
            const shiftId = active.data?.current?.shiftId;
            if (shiftId === undefined || shiftId === null) {
                setActiveShiftId(null);
                return;
            }

            let targetStaffId: string | null = null;
            let targetDay: string | null = null;
            const overId = String(over.id);

            if (overId.startsWith("cell:")) {
                const parts = overId.split(":");
                targetStaffId = parts[1] || null;
                targetDay = parts[2] || null;
            } else if (overId.startsWith("shift_")) {
                const overShift = shifts.find((s) => String(s.id) === overId.replace("shift_", ""));
                if (overShift) {
                    targetDay = overShift.day;
                    const targetStaff = staffMembers.find((s) => s.name === overShift.staff);
                    targetStaffId = targetStaff?.id || null;
                }
            }

            if (!targetStaffId || !targetDay) {
                setActiveShiftId(null);
                return;
            }
            const targetStaff = staffMembers.find((s) => s.id === targetStaffId);
            if (!targetStaff) {
                setActiveShiftId(null);
                return;
            }

            const nextShifts = shifts.map((shift) => {
                if (String(shift.id) !== String(shiftId)) return shift;
                return { ...shift, staff: targetStaff.name, day: targetDay };
            });

            setShifts(nextShifts);
            await persistStaffing(staffMembers, nextShifts);
        }
        setActiveShiftId(null);
    };

    const handleDragCancel = () => {
        setActiveShiftId(null);
    };

    const toggleStaffExpand = (staffId: string) => {
        setExpandedStaffIds((prev) => {
            const next = new Set(prev);
            if (next.has(staffId)) {
                next.delete(staffId);
            } else {
                next.add(staffId);
            }
            return next;
        });
    };

    const exportRota = () => {
        const dayHeaders = days.map((d) => d.name);
        const rows = staffMembers.map((staff) => {
            const cells = dayHeaders.map((day) => {
                const dayShifts = shifts.filter((s) => s.staff === staff.name && s.day === day);
                if (dayShifts.length === 0) return "";
                return dayShifts.map((s) => `${s.startTime}-${s.endTime} ${s.role}`).join("<br/>");
            });
            return `<tr>
                <td style="padding:8px 10px; border:1px solid #e2e8f0; font-weight:600;">${staff.name}</td>
                ${cells.map((c) => `<td style="padding:8px 10px; border:1px solid #e2e8f0; font-size:12px;">${c || "-"}</td>`).join("")}
            </tr>`;
        }).join("");

        const html = `
            <html>
              <head>
                <title>Staffing Rota</title>
                <style>
                  body { font-family: Inter, Arial, sans-serif; padding: 24px; color: #0f172a; }
                  h1 { font-size: 20px; margin-bottom: 12px; }
                  table { width: 100%; border-collapse: collapse; font-size: 12px; }
                  th { text-align: left; padding: 8px 10px; background: #f8fafc; border: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.08em; font-size: 10px; }
                </style>
              </head>
              <body>
                <h1>Staffing Rota</h1>
                <table>
                  <thead>
                    <tr>
                      <th>Staff Member</th>
                      ${dayHeaders.map((d) => `<th>${d}</th>`).join("")}
                    </tr>
                  </thead>
                  <tbody>
                    ${rows}
                  </tbody>
                </table>
              </body>
            </html>
        `;

        const win = window.open("", "print", "width=1024,height=768");
        if (!win) return;
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 300);
    };

    const draftDailyWage = dailyWageFromSalary(staffDraft.salary);
    const draftHourlyRate = staffDraft.salary > 0 && staffDraft.contractedHours > 0
        ? staffDraft.salary / (staffDraft.contractedHours * 52)
        : 0;

    useEffect(() => {
        let isMounted = true;
        if (!supabase || !_user?.id) {
            setLoading(false);
            return;
        }
        supaFetchManualStaffing(_user.id).then((data) => {
            if (!isMounted) return;
            if (data) {
                if (Array.isArray(data.staffMembers)) {
                    const normalized = data.staffMembers.map((entry: any, idx: number) =>
                        typeof entry === "string"
                            ? normalizeStaffMember({ id: `staff_${idx}`, name: entry })
                            : normalizeStaffMember(entry)
                    );
                    const withColors = assignMissingStaffColors(normalized);
                    setStaffMembers(withColors.length > 0 ? withColors : assignMissingStaffColors(DEFAULT_STAFF));
                    if (withColors.length > 0 && JSON.stringify(withColors) !== JSON.stringify(normalized)) {
                        persistStaffing(withColors, Array.isArray(data.shifts) ? data.shifts : shifts);
                    }
                }
                if (Array.isArray(data.shifts)) setShifts(data.shifts);
            }
            setLoading(false);
        });
        return () => { isMounted = false; };
    }, [_user?.id]);

    useEffect(() => {
        if (!lastShift && shifts.length > 0) {
            setLastShift(shifts[shifts.length - 1]);
        }
    }, [shifts, lastShift]);

    const persistStaffing = async (nextStaff: StaffMember[], nextShifts: any[]) => {
        if (_user?.id) {
            await supaSaveManualStaffing(_user.id, _venueId || "", { staffMembers: nextStaff, shifts: nextShifts });
        }
    };

    const openStaffModal = (staff?: StaffMember) => {
        if (staff) {
            setStaffDraft(normalizeStaffMember({ ...staff, availability: staff.availability, performance: staff.performance }));
            setEditingStaffId(staff.id);
        } else {
            const draft = normalizeStaffMember({ name: "", role: "FOH" });
            setStaffDraft({ ...draft, name: "" });
            setEditingStaffId(null);
        }
        setShowStaffModal(true);
    };

    const updateStaffDraftAvailability = (day: string, patch: Partial<StaffAvailability>) => {
        setStaffDraft((prev) => ({
            ...prev,
            availability: {
                ...prev.availability,
                [day]: { ...prev.availability[day], ...patch }
            }
        }));
    };

    const saveStaffDetails = async () => {
        const trimmedName = staffDraft.name.trim();
        if (!trimmedName) return;
        const duplicate = staffMembers.find((s) => s.name.toLowerCase() === trimmedName.toLowerCase() && s.id !== editingStaffId);
        if (duplicate) {
            alert("That staff member already exists.");
            return;
        }

        if (editingStaffId) {
            const prev = staffMembers.find((s) => s.id === editingStaffId);
            const prevName = prev?.name;
            const nextStaff = staffMembers.map((s) => s.id === editingStaffId ? { ...staffDraft, name: trimmedName } : s);
            const nextShifts = prevName && prevName !== trimmedName
                ? shifts.map((shift) => (shift.staff === prevName ? { ...shift, staff: trimmedName } : shift))
                : shifts;
            setStaffMembers(nextStaff);
            setShifts(nextShifts);
            await persistStaffing(nextStaff, nextShifts);
        } else {
            const used = new Set<number>();
            staffMembers.forEach((staff) => {
                const idx = normalizeColorIndex(staff.colorIndex);
                if (idx !== undefined) used.add(idx);
            });
            const assignedColor = pickAvailableColorIndex(used);
            const newStaff = normalizeStaffMember({ ...staffDraft, id: `staff_${Date.now()}`, name: trimmedName, colorIndex: assignedColor });
            const nextStaff = [...staffMembers, newStaff];
            setStaffMembers(nextStaff);
            await persistStaffing(nextStaff, shifts);
        }

        setShowStaffModal(false);
        setEditingStaffId(null);
    };

    const openShiftModal = (staff: string, day: string, shift?: any) => {
        if (shift) {
            setEditingShiftId(String(shift.id));
            setShiftDraft({
                staff: shift.staff || staff,
                role: shift.role || "FOH",
                startTime: shift.startTime || "",
                endTime: shift.endTime || "",
                day: shift.day || day,
                cost: shift.cost !== undefined && shift.cost !== null ? String(shift.cost) : "",
                type: shift.type || "Added"
            });
        } else {
            setEditingShiftId(null);
            setShiftDraft({
                staff,
                role: "FOH",
                startTime: "",
                endTime: "",
                day,
                cost: "",
                type: "Added"
            });
        }
        setShowShiftModal(true);
    };

    const saveShift = async () => {
        const trimmedStaff = shiftDraft.staff.trim();
        if (!trimmedStaff || !shiftDraft.startTime || !shiftDraft.endTime) return;
        const updatedShift = {
            id: editingShiftId ? editingShiftId : Date.now(),
            staff: trimmedStaff,
            role: shiftDraft.role,
            startTime: shiftDraft.startTime,
            endTime: shiftDraft.endTime,
            day: shiftDraft.day,
            cost: Number(shiftDraft.cost) || 0,
            type: shiftDraft.type || "Added"
        };
        const nextShifts = editingShiftId
            ? shifts.map((shift) => String(shift.id) === String(editingShiftId) ? { ...shift, ...updatedShift, id: shift.id } : shift)
            : [...shifts, updatedShift];
        const exists = staffMembers.some((s) => s.name.toLowerCase() === trimmedStaff.toLowerCase());
        const nextStaff = exists
            ? staffMembers
            : (() => {
                const used = new Set<number>();
                staffMembers.forEach((staff) => {
                    const idx = normalizeColorIndex(staff.colorIndex);
                    if (idx !== undefined) used.add(idx);
                });
                const assignedColor = pickAvailableColorIndex(used);
                return [...staffMembers, normalizeStaffMember({ name: trimmedStaff, role: shiftDraft.role || "FOH", colorIndex: assignedColor })];
            })();
        setShifts(nextShifts);
        setStaffMembers(nextStaff);
        setShowShiftModal(false);
        if (!editingShiftId) {
            setLastShift(updatedShift);
            setShowAdjustment(true);
        }
        setEditingShiftId(null);
        await persistStaffing(nextStaff, nextShifts);
    };

    const removeStaff = async (staffId: string) => {
        const staff = staffMembers.find((s) => s.id === staffId);
        if (!staff) return;
        setRemoveTarget(staff);
        setShowRemoveModal(true);
    };

    const confirmRemoveStaff = async () => {
        if (!removeTarget) return;
        const nextStaff = staffMembers.filter((s) => s.id !== removeTarget.id);
        const nextShifts = shifts.filter((shift) => shift.staff !== removeTarget.name);
        setStaffMembers(nextStaff);
        setShifts(nextShifts);
        setShowRemoveModal(false);
        setRemoveTarget(null);
        await persistStaffing(nextStaff, nextShifts);
    };

    const handleStaffAvatarChange = (file?: File | null) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            setStaffDraft((prev) => ({ ...prev, avatarUrl: result }));
        };
        reader.readAsDataURL(file);
    };

    const updateStaffPerformance = async (staffId: string, patch: Partial<StaffPerformance>) => {
        const nextStaff = staffMembers.map((staff) =>
            staff.id === staffId
                ? { ...staff, performance: { ...staff.performance, ...patch } }
                : staff
        );
        setStaffMembers(nextStaff);
        await persistStaffing(nextStaff, shifts);
    };

    if (loading) return React.createElement("div", { className: "p-8 text-center" }, "Loading Staffing...");

    return React.createElement("div", { className: "space-y-6 animate-in fade-in duration-500 pb-12" }, [
        showRemoveModal && React.createElement("div", { key: "remove-modal", className: "fixed inset-0 z-50 flex items-center justify-center p-4" }, [
            React.createElement("div", { className: "absolute inset-0 bg-slate-900/40 backdrop-blur-sm", onClick: () => setShowRemoveModal(false) }),
            React.createElement("div", { className: "relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4" }, [
                React.createElement("div", { className: "p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/70" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, "Confirm Removal"),
                        React.createElement("h3", { className: "text-lg font-black text-slate-900" }, "Remove Staff Member")
                    ]),
                    React.createElement("button", { className: "text-slate-400 hover:text-slate-600", onClick: () => setShowRemoveModal(false) }, "✕")
                ]),
                React.createElement("div", { className: "p-6" }, [
                    React.createElement("p", { className: "text-sm text-slate-600" },
                        `Remove ${removeTarget?.name || "this staff member"} and all assigned shifts?`
                    )
                ]),
                React.createElement("div", { className: "p-5 border-t border-slate-100 flex justify-end gap-3 bg-white" }, [
                    React.createElement("button", { className: "px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-black uppercase tracking-widest hover:bg-slate-50", onClick: () => setShowRemoveModal(false) }, "Cancel"),
                    React.createElement("button", { className: "px-5 py-2 rounded-xl bg-rose-600 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-rose-100/50 hover:bg-rose-700", onClick: confirmRemoveStaff }, "Remove")
                ])
            ])
        ]),
        showStaffModal && React.createElement("div", { key: "staff-modal", className: "fixed inset-0 z-50 flex items-center justify-center p-4" }, [
            React.createElement("div", { className: "absolute inset-0 bg-slate-900/40 backdrop-blur-sm", onClick: () => setShowStaffModal(false) }),
            React.createElement("div", { className: "relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4" }, [
                React.createElement("div", { className: "p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/70" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, editingStaffId ? "Edit Staff" : "Add Staff"),
                        React.createElement("h3", { className: "text-lg font-black text-slate-900" }, "Staff Details")
                    ]),
                    React.createElement("button", { className: "text-slate-400 hover:text-slate-600", onClick: () => setShowStaffModal(false) }, "✕")
                ]),
                React.createElement("div", { className: "p-6 space-y-6 max-h-[70vh] overflow-y-auto" }, [
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Full Name"),
                            React.createElement("input", {
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                placeholder: "",
                                value: staffDraft.name,
                                onChange: (e: any) => setStaffDraft({ ...staffDraft, name: e.target.value })
                            })
                        ]),
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Role"),
                            React.createElement("select", {
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: staffDraft.role,
                                onChange: (e: any) => setStaffDraft({ ...staffDraft, role: e.target.value })
                            }, [
                                React.createElement("option", { value: "FOH" }, "FOH"),
                                React.createElement("option", { value: "Kitchen" }, "Kitchen"),
                                React.createElement("option", { value: "Supervisor" }, "Supervisor"),
                                React.createElement("option", { value: "Bar" }, "Bar")
                            ])
                        ])
                    ]),
                    React.createElement("div", { className: "bg-white border border-slate-200 rounded-2xl p-4" }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3" }, "Staff Avatar"),
                        React.createElement("div", { className: "flex flex-col sm:flex-row sm:items-center gap-4" }, [
                            React.createElement("div", { className: "w-16 h-16 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center" },
                                staffDraft.avatarUrl
                                    ? React.createElement("img", { src: staffDraft.avatarUrl, alt: staffDraft.name || "Staff avatar", className: "w-full h-full object-cover" })
                                    : React.createElement("span", { className: "text-xs font-black text-slate-400" }, "No image")
                            ),
                            React.createElement("div", { className: "flex flex-wrap items-center gap-3" }, [
                                React.createElement("label", {
                                    className: "px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-black uppercase tracking-widest hover:bg-slate-50 cursor-pointer"
                                }, [
                                    "Upload Image",
                                    React.createElement("input", {
                                        type: "file",
                                        accept: "image/*",
                                        className: "hidden",
                                        onChange: (e: any) => handleStaffAvatarChange(e.target.files?.[0])
                                    })
                                ]),
                                staffDraft.avatarUrl && React.createElement("button", {
                                    className: "px-3 py-2 rounded-xl border border-slate-200 text-slate-400 text-xs font-black uppercase tracking-widest hover:text-rose-600 hover:border-rose-200",
                                    onClick: () => setStaffDraft({ ...staffDraft, avatarUrl: "" })
                                }, "Remove")
                            ]),
                            React.createElement("p", { className: "text-[11px] text-slate-400" }, "JPEG or PNG, square images work best.")
                        ])
                    ]),
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Annual Salary (£)"),
                            React.createElement("input", {
                                type: "number",
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: staffDraft.salary === 0 ? "" : staffDraft.salary,
                                onChange: (e: any) => setStaffDraft({ ...staffDraft, salary: e.target.value === "" ? 0 : Number(e.target.value) })
                            })
                        ]),
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Contracted Hours (Weekly)"),
                            React.createElement("input", {
                                type: "number",
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: staffDraft.contractedHours === 0 ? "" : staffDraft.contractedHours,
                                onChange: (e: any) => setStaffDraft({ ...staffDraft, contractedHours: e.target.value === "" ? 0 : Number(e.target.value) })
                            })
                        ])
                    ]),
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
                        React.createElement("div", { className: "bg-slate-50 border border-slate-200 rounded-2xl p-4" }, [
                            React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Estimated Daily Wage"),
                            React.createElement("p", { className: "text-lg font-black text-slate-900 mt-2" }, draftDailyWage > 0 ? formatMoney(draftDailyWage) : "—"),
                            React.createElement("p", { className: "text-[11px] text-slate-500 mt-1" }, "Based on annual salary")
                        ]),
                        React.createElement("div", { className: "bg-slate-50 border border-slate-200 rounded-2xl p-4" }, [
                            React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Estimated Hourly Rate"),
                            React.createElement("p", { className: "text-lg font-black text-slate-900 mt-2" }, draftHourlyRate > 0 ? `£${draftHourlyRate.toFixed(2)}` : "—"),
                            React.createElement("p", { className: "text-[11px] text-slate-500 mt-1" }, "Uses contracted hours")
                        ])
                    ]),
                    React.createElement("div", { className: "bg-white border border-slate-200 rounded-2xl p-4" }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3" }, "Availability"),
                        React.createElement("div", { className: "space-y-2" },
                            STAFF_DAYS.map((day) => {
                                const dayAvail = staffDraft.availability[day];
                                return React.createElement("div", { key: day, className: "grid grid-cols-1 md:grid-cols-[120px_1fr_1fr] gap-3 items-center" }, [
                                    React.createElement("label", { className: "flex items-center gap-2 text-sm font-semibold text-slate-700" }, [
                                        React.createElement("input", {
                                            type: "checkbox",
                                            checked: dayAvail.available,
                                            onChange: (e: any) => updateStaffDraftAvailability(day, { available: e.target.checked }),
                                            className: "h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        }),
                                        day
                                    ]),
                                    React.createElement("input", {
                                        type: "time",
                                        disabled: !dayAvail.available,
                                        value: dayAvail.start,
                                        onChange: (e: any) => updateStaffDraftAvailability(day, { start: e.target.value }),
                                        className: "w-full p-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm disabled:bg-slate-100"
                                    }),
                                    React.createElement("input", {
                                        type: "time",
                                        disabled: !dayAvail.available,
                                        value: dayAvail.end,
                                        onChange: (e: any) => updateStaffDraftAvailability(day, { end: e.target.value }),
                                        className: "w-full p-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm disabled:bg-slate-100"
                                    })
                                ]);
                            })
                        )
                    ])
                ]),
                React.createElement("div", { className: "p-5 border-t border-slate-100 flex justify-end gap-3 bg-white" }, [
                    React.createElement("button", { className: "px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-black uppercase tracking-widest hover:bg-slate-50", onClick: () => setShowStaffModal(false) }, "Cancel"),
                    React.createElement("button", { className: "px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 hover:bg-indigo-700", onClick: saveStaffDetails }, "Save Staff")
                ])
            ])
        ]),

        showShiftModal && React.createElement("div", { key: "shift-modal", className: "fixed inset-0 z-50 flex items-center justify-center p-4" }, [
            React.createElement("div", { className: "absolute inset-0 bg-slate-900/40 backdrop-blur-sm", onClick: () => setShowShiftModal(false) }),
            React.createElement("div", { className: "relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4" }, [
                React.createElement("div", { className: "p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/70" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1" }, editingShiftId ? "Edit Shift" : "Add Shift"),
                        React.createElement("h3", { className: "text-lg font-black text-slate-900" }, `${shiftDraft.day} Shift`)
                    ]),
                    React.createElement("button", { className: "text-slate-400 hover:text-slate-600", onClick: () => setShowShiftModal(false) }, "✕")
                ]),
                React.createElement("div", { className: "p-6 space-y-4" }, [
                    React.createElement("div", {}, [
                        React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Staff member"),
                        React.createElement("input", {
                            className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                            value: shiftDraft.staff,
                            onChange: (e: any) => setShiftDraft({ ...shiftDraft, staff: e.target.value })
                        })
                    ]),
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Role"),
                            React.createElement("select", {
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: shiftDraft.role,
                                onChange: (e: any) => setShiftDraft({ ...shiftDraft, role: e.target.value })
                            }, [
                                React.createElement("option", { value: "FOH" }, "FOH"),
                                React.createElement("option", { value: "Kitchen" }, "Kitchen"),
                                React.createElement("option", { value: "Supervisor" }, "Supervisor"),
                                React.createElement("option", { value: "Bar" }, "Bar")
                            ])
                        ]),
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Cost (£)"),
                            React.createElement("input", {
                                type: "number",
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: shiftDraft.cost,
                                onChange: (e: any) => setShiftDraft({ ...shiftDraft, cost: e.target.value })
                            })
                        ])
                    ]),
                    React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Start time"),
                            React.createElement("input", {
                                type: "time",
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: shiftDraft.startTime,
                                onChange: (e: any) => setShiftDraft({ ...shiftDraft, startTime: e.target.value })
                            })
                        ]),
                        React.createElement("div", {}, [
                            React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "End time"),
                            React.createElement("input", {
                                type: "time",
                                className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                value: shiftDraft.endTime,
                                onChange: (e: any) => setShiftDraft({ ...shiftDraft, endTime: e.target.value })
                            })
                        ])
                    ])
                ]),
                React.createElement("div", { className: "p-5 border-t border-slate-100 flex justify-end gap-3 bg-white" }, [
                    React.createElement("button", { className: "px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-black uppercase tracking-widest hover:bg-slate-50", onClick: () => setShowShiftModal(false) }, "Cancel"),
                    React.createElement("button", { className: "px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 hover:bg-indigo-700", onClick: saveShift }, editingShiftId ? "Update Shift" : "Save Shift")
                ])
            ])
        ]),
        // Page Title & Header
        React.createElement("div", { key: "header-main", className: "mb-8 bg-gradient-to-br from-indigo-50 to-white p-8 rounded-[32px] border border-indigo-100/50 shadow-sm flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" }, [
            React.createElement("div", {}, [
                React.createElement("h1", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Staffing & Rota Optimization"),
                React.createElement("p", { className: "text-gray-500 font-medium max-w-2xl leading-relaxed" },
                    "Balance your labour costs with peak demand. Findsy analyzes historical traffic and weather data to suggest optimal shift counts, helping you maintain high service levels without overspending on payroll."
                )
            ]),
            React.createElement("div", { className: "w-full lg:w-auto" }, [
                React.createElement("div", { key: "summary", className: "bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" }, [
                    React.createElement("div", { className: "flex flex-wrap gap-6" }, [
                        React.createElement("div", { key: "cost" }, [
                            React.createElement("p", { className: "text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1" }, "Weekly Labour Forecast"),
                            React.createElement("p", { className: "text-xl font-black text-gray-900" }, stats.weeklyCost)
                        ]),
                        React.createElement("div", { key: "percent" }, [
                            React.createElement("p", { className: "text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1" }, "Labour % vs Rev"),
                            React.createElement("div", { className: "flex items-baseline gap-2" }, [
                                React.createElement("p", { className: `text-xl font-black ${stats.labourPercent > stats.targetPercent ? 'text-red-600' : 'text-green-600'}` }, `${stats.labourPercent}%`),
                                React.createElement("p", { className: "text-xs text-gray-500 font-bold" }, `(Target ${stats.targetPercent}%)`)
                            ])
                        ]),
                        React.createElement("div", { key: "rev" }, [
                            React.createElement("p", { className: "text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1" }, "Forecasted Revenue"),
                            React.createElement("p", { className: "text-xl font-black text-gray-900" }, stats.forecastRev)
                        ])
                    ])
                ])
            ])
        ]),
        React.createElement("div", { key: "staffing-tabs", className: "flex gap-4 mb-6 border-b border-gray-200" }, [
            React.createElement("button", {
                onClick: () => setActiveTab("rota"),
                className: `pb-3 px-1 text-sm font-bold transition-all border-b-2 ${activeTab === "rota" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-400 hover:text-gray-600"}`
            }, "Rota & Forecast"),
            React.createElement("button", {
                onClick: () => setActiveTab("performance"),
                className: `pb-3 px-1 text-sm font-bold transition-all border-b-2 ${activeTab === "performance" ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-400 hover:text-gray-600"}`
            }, "Staff Performance")
        ]),

        // 2. STAFFING GUIDANCE & QUICK LINKS
        activeTab === "rota" && React.createElement("div", { key: "guidance-row", className: "hidden" }),

        // 2b. STAFF PERFORMANCE OVERVIEW
        activeTab === "performance" && React.createElement("div", { key: "performance-overview", className: "space-y-6" }, [
            React.createElement("div", { className: "bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" }, [
                React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Staffing Performance Overview"),
                        React.createElement("h3", { className: "text-lg font-black text-slate-900 mt-1" }, "Lateness, days off, illness (30 days)")
                    ]),
                    React.createElement("div", { className: "flex items-center gap-3 text-xs font-black uppercase tracking-widest" }, [
                        React.createElement("span", { className: "px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200" }, `Late: ${performanceOverview.totals.lateness}`),
                        React.createElement("span", { className: "px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" }, `Days Off: ${performanceOverview.totals.daysOff}`),
                        React.createElement("span", { className: "px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200" }, `Illness: ${performanceOverview.totals.illness}`)
                    ])
                ]),
                React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" }, [
                    React.createElement("div", { className: "p-4 bg-slate-50 rounded-xl border border-slate-200" }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Most Late"),
                        React.createElement("p", { className: "text-sm font-black text-slate-900 mt-1" }, performanceOverview.topLateness?.name || "—"),
                        React.createElement("p", { className: "text-xs text-slate-500" }, `${performanceOverview.topLateness?.performance?.lateness || 0} late arrivals`)
                    ]),
                    React.createElement("div", { className: "p-4 bg-slate-50 rounded-xl border border-slate-200" }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Most Days Off"),
                        React.createElement("p", { className: "text-sm font-black text-slate-900 mt-1" }, performanceOverview.topDaysOff?.name || "—"),
                        React.createElement("p", { className: "text-xs text-slate-500" }, `${performanceOverview.topDaysOff?.performance?.daysOff || 0} days off`)
                    ]),
                    React.createElement("div", { className: "p-4 bg-slate-50 rounded-xl border border-slate-200" }, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Most Illness"),
                        React.createElement("p", { className: "text-sm font-black text-slate-900 mt-1" }, performanceOverview.topIllness?.name || "—"),
                        React.createElement("p", { className: "text-xs text-slate-500" }, `${performanceOverview.topIllness?.performance?.illness || 0} illness days`)
                    ])
                ])
            ]),
            React.createElement("div", { className: "bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" }, [
                React.createElement("div", { className: "flex items-center justify-between mb-4" }, [
                    React.createElement("div", {}, [
                        React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, "Update Performance"),
                        React.createElement("h3", { className: "text-lg font-black text-slate-900 mt-1" }, "Staff Performance Inputs")
                    ])
                ]),
                React.createElement("div", { className: "space-y-4" },
                    staffMembers.map((staff) =>
                        React.createElement("div", { key: staff.id, className: "flex flex-col lg:flex-row lg:items-center gap-4 p-4 border border-slate-200 rounded-2xl bg-slate-50/60" }, [
                            React.createElement("div", { className: "flex items-center gap-3 min-w-[220px]" }, [
                                React.createElement("div", { className: "w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-black text-indigo-600" }, initialsForName(staff.name)),
                                React.createElement("div", {}, [
                                    React.createElement("p", { className: "text-sm font-black text-slate-900" }, staff.name),
                                    React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400" }, staff.role || "Role")
                                ])
                            ]),
                            React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1" }, [
                                React.createElement("div", {}, [
                                    React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Lateness"),
                                    React.createElement("input", {
                                        type: "number",
                                        className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                        value: staff.performance.lateness === 0 ? "" : staff.performance.lateness,
                                        onChange: (e: any) => updateStaffPerformance(staff.id, { lateness: e.target.value === "" ? 0 : Number(e.target.value) })
                                    })
                                ]),
                                React.createElement("div", {}, [
                                    React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Days Off"),
                                    React.createElement("input", {
                                        type: "number",
                                        className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                        value: staff.performance.daysOff === 0 ? "" : staff.performance.daysOff,
                                        onChange: (e: any) => updateStaffPerformance(staff.id, { daysOff: e.target.value === "" ? 0 : Number(e.target.value) })
                                    })
                                ]),
                                React.createElement("div", {}, [
                                    React.createElement("label", { className: "text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block" }, "Illness"),
                                    React.createElement("input", {
                                        type: "number",
                                        className: "w-full p-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500/15 focus:border-indigo-300 shadow-sm",
                                        value: staff.performance.illness === 0 ? "" : staff.performance.illness,
                                        onChange: (e: any) => updateStaffPerformance(staff.id, { illness: e.target.value === "" ? 0 : Number(e.target.value) })
                                    })
                                ])
                            ])
                        ])
                    )
                )
            ])
        ]),

        // 3. DAILY DEMAND STRIP
        activeTab === "rota" && React.createElement("div", { key: "demand-strip", className: "flex gap-2 overflow-x-auto pb-2 no-scrollbar" },
            days.map(day =>
                React.createElement("button", {
                    key: day.name,
                    onClick: () => setSelectedDay(day.name),
                    className: `flex-1 min-w-[140px] p-4 rounded-2xl border transition-all text-left relative overflow-hidden ${selectedDay === day.name ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-gray-100 bg-white hover:border-indigo-200'}`
                }, [
                    React.createElement("div", { className: "flex justify-between items-start mb-3" }, [
                        React.createElement("div", {}, [
                            React.createElement("span", { className: "font-black text-indigo-600" }, day.name),
                            React.createElement("span", { className: "block text-[10px] text-slate-400 font-semibold" }, day.dateLabel)
                        ]),
                        React.createElement("span", {}, day.weather)
                    ]),
                    React.createElement("div", { className: "flex items-baseline gap-2" }, [
                        React.createElement("p", { className: "text-2xl font-black text-slate-900" }, `${day.assigned}`),
                        React.createElement("p", { className: "text-[11px] font-bold text-slate-500 uppercase tracking-widest" }, "Assigned")
                    ]),
                    React.createElement("div", { className: "mt-2 flex items-center justify-between" }, [
                        React.createElement("div", {}, [
                            React.createElement("p", { className: "text-[10px] text-slate-400 font-bold uppercase tracking-widest" }, "Recommended"),
                            React.createElement("p", { className: "text-sm font-black text-slate-800" }, `${day.recommended}`)
                        ]),
                        React.createElement("span", {
                            className: `text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${day.gap < 0 ? "bg-rose-50 text-rose-600 border border-rose-200" : day.gap > 0 ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`
                        }, day.gap === 0 ? "On target" : `${day.gap > 0 ? "+" : ""}${day.gap} staff`)
                    ]),
                    React.createElement("div", { className: "mt-3 flex items-center justify-between text-[10px] text-slate-500 font-semibold" }, [
                        React.createElement("span", {}, `${day.covers} covers`),
                        React.createElement("span", {
                            className: `px-2 py-0.5 rounded uppercase tracking-tighter font-black ${day.intensity === "Peak" ? "bg-red-100 text-red-700" :
                                day.intensity === "High" ? "bg-orange-100 text-orange-700" :
                                    "bg-green-100 text-green-700"
                                }`
                        }, day.intensity)
                    ])
                ])
            )
        ),

        // 4. SHIFT GRID
        activeTab === "rota" && React.createElement("div", { key: "grid", className: "bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm flex flex-col" }, [
            React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 px-6 py-5 border-b border-gray-100 bg-white/80" }, [
                React.createElement("div", {}, [
                    React.createElement("h3", { className: "text-lg font-black text-slate-900" }, "Staffing Table"),
                    React.createElement("p", { className: "text-xs text-slate-500 mt-1" }, "Drag rows to reorder. Add staff, manage contracts, and print rota."),
                    React.createElement("div", { className: "mt-3 flex flex-wrap items-center gap-3" }, [
                        React.createElement("div", { className: "flex items-center gap-1 bg-slate-100 rounded-full p-1" }, [
                            React.createElement("button", {
                                onClick: () => { setCalendarView("week"); },
                                className: `px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest ${calendarView === "week" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`
                            }, "Week"),
                            React.createElement("button", {
                                onClick: () => { setCalendarView("month"); setMonthWeekIndex(0); },
                                className: `px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest ${calendarView === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`
                            }, "Month")
                        ]),
                        React.createElement("div", { className: "flex items-center gap-2 text-xs font-semibold text-slate-500" }, [
                            React.createElement("button", {
                                onClick: handlePrevRange,
                                className: "w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 flex items-center justify-center"
                            }, "‹"),
                            React.createElement("span", { className: "text-xs font-semibold text-slate-500" },
                                calendarView === "month" ? `${monthLabel} • Week ${Math.min(monthWeekIndex + 1, monthWeekStarts.length || 1)}` : weekRangeLabel
                            ),
                            React.createElement("button", {
                                onClick: handleNextRange,
                                className: "w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 hover:border-indigo-200 flex items-center justify-center"
                            }, "›")
                        ])
                    ]),
                    calendarView === "month" && monthWeekStarts.length > 1 && React.createElement("div", { className: "mt-3 flex flex-wrap gap-2" },
                        monthWeekStarts.map((start, idx) =>
                            React.createElement("button", {
                                key: `week-${idx}`,
                                onClick: () => setMonthWeekIndex(idx),
                                className: `px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${idx === monthWeekIndex ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-500 border-slate-200 hover:border-indigo-300"}`
                            }, `Week ${idx + 1} (${formatShortDate(start)})`)
                        )
                    )
                ]),
                React.createElement("div", { className: "flex items-center gap-3" }, [
                    React.createElement("button", {
                        className: "px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-100/60 hover:bg-indigo-700",
                        onClick: () => openStaffModal()
                    }, "Add Staff"),
                    React.createElement("button", {
                        className: "px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-black uppercase tracking-widest hover:bg-slate-50",
                        onClick: exportRota
                    }, "Export / Print")
                ])
            ]),
            React.createElement("div", { className: "flex border-b border-gray-100" }, [
                React.createElement("div", { className: "w-64 p-4 border-r border-gray-100 bg-gray-50/50" },
                    React.createElement("span", { className: "text-[10px] font-bold text-gray-400 uppercase" }, "Staff Member")
                ),
                days.map(d =>
                    React.createElement("div", { key: d.name, className: `flex-1 p-4 text-center border-r last:border-0 border-gray-100 ${selectedDay === d.name ? 'bg-indigo-50/50' : ''}` },
                        React.createElement("div", { className: "flex flex-col items-center gap-1" }, [
                            React.createElement("span", { className: `text-xs font-black ${selectedDay === d.name ? 'text-indigo-600' : 'text-gray-500'}` }, d.name),
                            React.createElement("span", { className: "text-[10px] font-semibold text-slate-400" }, d.dateLabel)
                        ])
                    )
                )
            ]),
            React.createElement("div", { className: "divide-y divide-gray-50" },
                React.createElement(DndContext, {
                    sensors,
                    collisionDetection: closestCenter,
                    onDragStart: handleDragStart,
                    onDragEnd: handleDragEnd,
                    onDragCancel: handleDragCancel
                },
                    React.createElement(SortableContext, {
                        items: staffMembers.map((s) => s.id),
                        strategy: verticalListSortingStrategy,
                        children: staffMembers.map((staff) =>
                            React.createElement(StaffRowSortable, {
                                key: staff.id,
                                staff,
                                days,
                                shifts,
                                selectedDay,
                                scheduledHours: staffScheduledHours[staff.id] || 0,
                                onOpenShiftModal: openShiftModal,
                                onEdit: openStaffModal,
                                onRemove: removeStaff,
                                expanded: expandedStaffIds.has(staff.id),
                                onToggleExpand: toggleStaffExpand
                            })
                        )
                    }),
                    React.createElement(DragOverlay, { adjustScale: false, dropAnimation: null },
                        activeShift ? buildShiftCard(activeShift, { className: "shadow-xl cursor-grabbing" }) : null
                    )
                )
            )
        ]),

        // 5. COST FEEDBACK (Dynamic UI element - showing as a floating alert or panel)
        showAdjustment && lastShift && React.createElement("div", { key: "feedback", className: "fixed bottom-8 right-8 w-80 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 animate-in slide-in-from-bottom-4" }, [
            React.createElement("div", { className: "flex justify-between items-start mb-3" }, [
                React.createElement("h5", { className: "font-black text-sm text-gray-900" }, "Live Shift Adjustment"),
                React.createElement("button", { className: "text-gray-400 hover:text-gray-600", onClick: () => setShowAdjustment(false) }, "✕")
            ]),
            React.createElement("div", { className: "space-y-3" }, [
                React.createElement("div", { className: "p-3 bg-indigo-50 rounded-lg border border-indigo-100" }, [
                    React.createElement("p", { className: "text-[10px] font-bold text-indigo-400 uppercase mb-1" }, `${lastShift.staff} • ${lastShift.day}`),
                    React.createElement("p", { className: "text-xs text-indigo-900 font-medium" }, `Adding ${lastShift.startTime} - ${lastShift.endTime} as ${lastShift.role}.`)
                ]),
                React.createElement("div", { className: "space-y-1" }, [
                    React.createElement("div", { className: "flex justify-between text-xs" }, [
                        React.createElement("span", { className: "text-gray-500" }, "Shift Cost:"),
                        React.createElement("span", { className: "font-bold text-gray-900" }, `£${Number(lastShift.cost || 0).toFixed(2)}`)
                    ]),
                    React.createElement("div", { className: "flex justify-between text-xs" }, [
                        React.createElement("span", { className: "text-gray-500" }, "Labour Impact:"),
                        React.createElement("span", { className: `font-bold ${labourPercent > targetPercent ? "text-red-600" : "text-emerald-600"}` }, `${labourPercent > targetPercent ? "+" : ""}${Math.abs(labourPercent - targetPercent)}% vs target`)
                    ]),
                    React.createElement("div", { className: "flex justify-between text-xs" }, [
                        React.createElement("span", { className: "text-gray-500" }, "Current Total:"),
                        React.createElement("span", { className: "font-bold text-gray-900" }, `${labourPercent}%`)
                    ])
                ]),
                React.createElement("button", { className: "w-full bg-indigo-600 text-white font-bold py-2 rounded-lg text-xs" }, "Confirm Shift")
            ])
        ]),

        // Footer / Quick Links & Legacy Section
        React.createElement("div", { key: "footer", className: "hidden" }),

        // Hidden Legacy Teams Section
        showLegacyTeams && React.createElement("div", { key: "legacy", className: "mt-8 p-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200 opacity-60" }, [
            React.createElement("h3", { className: "text-sm font-bold text-gray-400 uppercase mb-4" }, "Legacy Staff Groups (Paused)"),
            React.createElement("p", { className: "text-xs text-gray-500" }, "Chat and staff group features are currently hidden while the new Staffing tool is active.")
        ])
    ]);
}
